import * as React from 'react'
import { cn } from '@/lib/utils'

export interface SegmentedOption<T extends string> {
  value: T
  label: React.ReactNode
  /** Accessible name when `label` isn't plain text. */
  ariaLabel?: string
  disabled?: boolean
  /** `warning` (amber) marks a choice with real-world weight: test mode, live access. */
  tone?: 'default' | 'warning'
}

interface SegmentedControlProps<T extends string> {
  value: T
  onChange: (value: T) => void
  options: readonly SegmentedOption<T>[]
  ariaLabel: string
  /** Stretch to the container's width, options sharing it equally. */
  fullWidth?: boolean
  className?: string
  id?: string
  'aria-describedby'?: string
}

/**
 * A small set of mutually exclusive choices, side by side (a radio group).
 * Arrow keys move the choice; only the selected option is in the tab order.
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  fullWidth = false,
  className,
  id,
  'aria-describedby': describedBy,
}: SegmentedControlProps<T>) {
  const optionsRef = React.useRef<(HTMLButtonElement | null)[]>([])
  const enabled = options.filter((option) => !option.disabled)

  function move(from: T, step: 1 | -1): void {
    const index = enabled.findIndex((option) => option.value === from)
    const next = enabled[(index + step + enabled.length) % enabled.length]
    if (!next) return
    onChange(next.value)
    optionsRef.current[options.findIndex((option) => option.value === next.value)]?.focus()
  }

  return (
    <div
      id={id}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-describedby={describedBy}
      className={cn(
        'grid auto-cols-fr grid-flow-col gap-0.5 rounded-md border border-border bg-subtle p-0.5',
        fullWidth ? 'w-full' : 'inline-grid',
        className,
      )}
    >
      {options.map((option, index) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            ref={(node) => {
              optionsRef.current[index] = node
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={option.ariaLabel}
            disabled={option.disabled}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault()
                move(option.value, 1)
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault()
                move(option.value, -1)
              }
            }}
            className={cn(
              'inline-flex min-h-7 min-w-0 items-center justify-center gap-1.5 rounded-sm px-2.5 py-1 text-center text-caption leading-tight font-medium text-balance transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:size-3.5',
              selected && option.tone !== 'warning' && 'bg-card text-foreground shadow-card',
              selected && option.tone === 'warning' && 'bg-warning-subtle text-warning shadow-card',
              !selected && 'text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
